// components/patient/BundleOrder.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { useCart } from '../../contexts/CartContext';
import { cartService } from '../../services/cartService';
import { gqlRequest } from '../../services/api';
import { DB } from '../../utils/db';
import { BraintreePayment } from '../shared/BraintreePayment';

export function BundleOrder({ user, onNavigate, onRefresh }) {
  const { brand } = useBrand();
  const toast = useToast();
  const { addItem, clearCart } = useCart();
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [paymentMethods, setPaymentMethods] = useState([
    { code: 'checkmo', title: 'Check / Money Order' },
    { code: 'purchaseorder', title: 'Purchase Order' }
  ]);
  const [selectedPayment, setSelectedPayment] = useState('checkmo');
  const [braintreeNonce, setBraintreeNonce] = useState(null);
  const [braintreeReady, setBraintreeReady] = useState(false);
  const braintreeRef = useRef(null);

  const GATEWAY_CODES = ['braintree'];
  const BRAINTREE_METHODS = ['braintree', 'braintree_ach', 'braintree_paypal'];

  const flattenPaymentMethods = (methods) => {
    const result = [];
    for (const m of methods) {
      if (GATEWAY_CODES.includes(m.code) && m.methods && m.methods.length > 0) {
        for (const sub of m.methods) {
          if (sub.code === 'braintree_cc_vault') continue;
          result.push({
            code: sub.code,
            title: sub.code === 'braintree' ? 'Credit Card' : sub.title,
            gateway: m.code
          });
        }
      } else {
        result.push(m);
      }
    }
    return result;
  };

  const getPaymentIcon = (code) => {
    switch (code) {
      case 'braintree':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><rect x="4" y="4" width="24" height="3" rx="1" fill="#1A56DB"/><rect x="4" y="13" width="8" height="3" rx="1" fill="#D1D5DB"/></svg>;
      case 'braintree_ach':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><path d="M8 6h16M8 10h16M8 14h10" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/></svg>;
      case 'braintree_apple_pay':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><text x="16" y="14" textAnchor="middle" fill="#000" fontSize="10" fontFamily="system-ui" fontWeight="600">Pay</text></svg>;
      case 'braintree_paypal':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><text x="16" y="14" textAnchor="middle" fill="#003087" fontSize="8" fontFamily="system-ui" fontWeight="700">PayPal</text></svg>;
      case 'billcom':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><text x="16" y="14" textAnchor="middle" fill="#00B4E6" fontSize="8" fontFamily="system-ui" fontWeight="700">bill</text></svg>;
      case 'checkmo':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><path d="M8 10l3 3 6-6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
      default:
        return null;
    }
  };
  
  useEffect(() => {
    loadPrescriptions();
    loadPaymentMethods();
  }, [user.id]);

  const loadPaymentMethods = async () => {
    const bgMethods = user?.buyerGroup?.paymentMethods;
    if (bgMethods && bgMethods.length > 0) {
      const flat = flattenPaymentMethods(bgMethods);
      setPaymentMethods(flat);
      setSelectedPayment(flat[0].code);
      return;
    }
    try {
      const profileData = await gqlRequest(`
        query { userByToken { buyerGroup { paymentMethods { id code title methods { code title } } } } }
      `, {}, { requireAuth: true });
      const methods = profileData?.userByToken?.buyerGroup?.paymentMethods;
      if (methods && methods.length > 0) {
        const flat = flattenPaymentMethods(methods);
        setPaymentMethods(flat);
        setSelectedPayment(flat[0].code);
        return;
      }
    } catch (e) {
      console.warn('Failed to fetch buyer group payment methods:', e.message);
    }
    try {
      const data = await gqlRequest(`
        query PaymentMethods {
          paymentMethods { code title }
        }
      `);
      const methods = data.paymentMethods || [];
      if (methods.length > 0) {
        setPaymentMethods(methods);
        setSelectedPayment(methods[0].code);
      }
    } catch (e) {
      // Keep defaults
    }
  };
  
  const loadPrescriptions = async () => {
    const allRxs = await DB.get('d2p_prescriptions') || [];
    const userRxs = allRxs.filter(rx => 
      rx.userId === user.id && 
      rx.status === 'verified' &&
      (rx.refills === undefined || rx.refills > 0)
    );
    setPrescriptions(userRxs);
  };
  
  const toggleSelection = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };
  
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.addToast('Please select at least one prescription', 'error');
      return;
    }
    if (BRAINTREE_METHODS.includes(selectedPayment) && !braintreeNonce) {
      if (braintreeRef.current) {
        try {
          const result = await braintreeRef.current.requestNonce();
          setBraintreeNonce(result.nonce);
        } catch (e) {
          toast.addToast('Please complete the payment form before placing your order.', 'error');
          return;
        }
      } else {
        toast.addToast('Payment form is not ready. Please wait or select a different payment method.', 'error');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const manualOrderData = {
        email: user?.email || '',
        items: selectedPrescriptions.map(rx => ({
          sku: rx.id || `RX-${rx.medication?.replace(/\s+/g, '-')}`,
          name: rx.medication || 'Prescription Medication',
          price: PRICE_PER_FILL + PHARMACY_FEE,
          total: PRICE_PER_FILL + PHARMACY_FEE,
          qty: 1
        })),
        totals: {
          subtotal,
          discount: 0,
          tax: 0,
          fee: 0,
          shipping: shippingCost,
          grand: total
        },
        paymentInformation: {
          methodTitle: paymentMethods.find(m => m.code === selectedPayment)?.title || 'Check / Money Order',
          methodCode: selectedPayment
        }
      };

      const shippingAddr = {
        firstname: user?.firstName || '',
        lastname: user?.lastName || '',
        street: user?.address?.street || '123 Main St',
        city: user?.address?.city || 'New York',
        regionCode: user?.address?.state || 'NY',
        countryId: 'US',
        postcode: user?.address?.zip || '10001',
        telephone: user?.phone || '',
        email: user?.email || ''
      };

      const result = await cartService.createOrder({
        manualOrderData,
        shippingAddress: shippingAddr,
        billingAddress: shippingAddr,
        paymentMethod: { method: selectedPayment, ...(braintreeNonce ? { additionalData: { nonce: braintreeNonce } } : {}) },
        userId: user?.id || user?.originUserId
      });

      const allRxs = await DB.get('d2p_prescriptions') || [];
      const updatedRxs = allRxs.map(rx => {
        if (selectedIds.includes(rx.id) && rx.refills !== undefined) {
          return { ...rx, refills: rx.refills - 1, lastFilledAt: new Date().toISOString() };
        }
        return rx;
      });
      await DB.set('d2p_prescriptions', updatedRxs);

      clearCart();
      toast.addToast('Order placed successfully!');
      onRefresh && onRefresh();
      onNavigate('order-detail', result?.id || 'latest');
    } catch (err) {
      console.warn('API order failed, falling back to localStorage:', err.message);
      const orderId = `ORD-${Date.now()}`;
      const order = {
        id: orderId,
        userId: user.id,
        prescriptionIds: selectedIds,
        items: selectedPrescriptions.map(rx => ({
          sku: rx.id,
          name: rx.medication || 'Prescription Medication',
          price: PRICE_PER_FILL,
          total: PRICE_PER_FILL,
          qty: 1
        })),
        totals: {
          itemSubtotal,
          pharmacyFee: pharmacyFeeTotal,
          subtotal,
          shipping: shippingCost,
          grand: total
        },
        brandKey: brand.key,
        shippingMethod,
        status: 'submitted',
        timeline: [{ status: 'submitted', timestamp: new Date().toISOString() }],
        createdAt: new Date().toISOString()
      };
      const orders = await DB.get('d2p_orders') || [];
      orders.push(order);
      await DB.set('d2p_orders', orders);

      const allRxs = await DB.get('d2p_prescriptions') || [];
      const updatedRxs = allRxs.map(rx => {
        if (selectedIds.includes(rx.id) && rx.refills !== undefined) {
          return { ...rx, refills: rx.refills - 1, lastFilledAt: new Date().toISOString() };
        }
        return rx;
      });
      await DB.set('d2p_prescriptions', updatedRxs);

      toast.addToast('Order placed (offline mode)');
      onRefresh && onRefresh();
      onNavigate('order-detail', orderId);
    } finally {
      setSubmitting(false);
    }
  };
  
  const shippingOptions = [
    { value: 'standard', label: 'Standard (2-3 days)', cost: 0, icon: 'truck' },
    { value: 'express', label: 'Express (1-2 days)', cost: 9.99, icon: 'zap' }
  ];
  
  const selectedPrescriptions = prescriptions.filter(rx => selectedIds.includes(rx.id));
  const PRICE_PER_FILL = 12;
  const PHARMACY_FEE = 4;
  const itemSubtotal = selectedPrescriptions.length * PRICE_PER_FILL;
  const pharmacyFeeTotal = selectedPrescriptions.length * PHARMACY_FEE;
  const subtotal = itemSubtotal + pharmacyFeeTotal;
  const shippingCost = shippingMethod === 'express' ? 9.99 : 0;
  const total = subtotal + shippingCost;
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('prescriptions')}
          style={{ marginBottom: 24 }}
        >
          Back to Prescriptions
        </Button>
        
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
          Bundle Order
        </h1>
        <p style={{ color: '#6B7280', marginBottom: 32 }}>
          Select prescriptions to order together and save on shipping
        </p>
        
        {prescriptions.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Icon name="package" size={40} color="#9CA3AF" />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                No Verified Prescriptions
              </h3>
              <p style={{ color: '#6B7280', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
                You need verified prescriptions with available refills to place an order
              </p>
              <Button icon="upload" onClick={() => onNavigate('upload')}>
                Upload Prescription
              </Button>
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 24,
            alignItems: 'start'
          }}>
            {/* Left Column - Prescription Selection */}
            <div>
              <Card style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                  Select Prescriptions ({selectedIds.length} selected)
                </h2>
                
                <div style={{ display: 'grid', gap: 12 }}>
                  {prescriptions.map(rx => (
                    <label
                      key={rx.id}
                      style={{
                        padding: 16,
                        border: `2px solid ${selectedIds.includes(rx.id) ? brand.c.accent : '#E5E7EB'}`,
                        borderRadius: 12,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: selectedIds.includes(rx.id) ? `${brand.c.accent}05` : 'white',
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(rx.id)}
                        onChange={() => toggleSelection(rx.id)}
                        style={{ width: 20, height: 20, cursor: 'pointer' }}
                      />
                      
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, marginBottom: 4, color: brand.c.navy }}>
                          {rx.medication}
                        </div>
                        <div style={{ fontSize: 14, color: '#6B7280' }}>
                          {rx.quantity} • {rx.refills} refill{rx.refills !== 1 ? 's' : ''} remaining
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: brand.c.accent }}>
                          ${PRICE_PER_FILL.toFixed(2)}
                        </div>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>
                          per fill
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </Card>

              {/* Pharmacy Services Fee */}
              <Card style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, color: brand.c.navy }}>
                  Pharmacy Services Fee
                </h2>
                <div style={{
                  padding: 16,
                  background: '#F9FAFB',
                  borderRadius: 12,
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 14, color: '#374151', fontWeight: 600 }}>
                      Fee per prescription
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: brand.c.navy }}>
                      ${PHARMACY_FEE.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
                    A pharmacy services fee is applied to each prescription to cover dispensing, verification, and compliance services.
                  </div>
                </div>
              </Card>

              {/* Payment Method */}
              <Card style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                  Payment Method
                </h2>
                <div style={{ display: 'grid', gap: 12 }}>
                  {paymentMethods.map(method => (
                    <label
                      key={method.code}
                      style={{
                        padding: 16,
                        border: `2px solid ${selectedPayment === method.code ? brand.c.accent : '#E5E7EB'}`,
                        borderRadius: 12,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: selectedPayment === method.code ? `${brand.c.accent}05` : 'white',
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center'
                      }}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value={method.code}
                        checked={selectedPayment === method.code}
                        onChange={(e) => {
                          setSelectedPayment(e.target.value);
                          setBraintreeNonce(null);
                          setBraintreeReady(false);
                        }}
                        style={{ width: 20, height: 20, cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, marginBottom: 2 }}>
                          {method.title}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        {getPaymentIcon(method.code)}
                      </div>
                    </label>
                  ))}
                  {BRAINTREE_METHODS.includes(selectedPayment) && (
                    <BraintreePayment
                      ref={braintreeRef}
                      selectedMethod={selectedPayment}
                      onNonce={({ nonce }) => setBraintreeNonce(nonce)}
                      onReady={(ready) => setBraintreeReady(ready)}
                      onError={(err) => console.warn('Braintree error:', err)}
                    />
                  )}
                </div>
              </Card>
              
              {/* Shipping Method */}
              <Card>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                  Shipping Method
                </h2>
                
                <div style={{ display: 'grid', gap: 12 }}>
                  {shippingOptions.map(option => (
                    <label
                      key={option.value}
                      style={{
                        padding: 16,
                        border: `2px solid ${shippingMethod === option.value ? brand.c.accent : '#E5E7EB'}`,
                        borderRadius: 12,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: shippingMethod === option.value ? `${brand.c.accent}05` : 'white',
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center'
                      }}
                    >
                      <input
                        type="radio"
                        name="shipping"
                        value={option.value}
                        checked={shippingMethod === option.value}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        style={{ width: 20, height: 20, cursor: 'pointer' }}
                      />
                      
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: `${brand.c.accent}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon name={option.icon} size={20} color={brand.c.accent} />
                      </div>
                      
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, marginBottom: 2 }}>
                          {option.label}
                        </div>
                        <div style={{ fontSize: 13, color: '#6B7280' }}>
                          UPS Healthcare 3PL
                        </div>
                      </div>
                      
                      <div style={{ fontSize: 18, fontWeight: 700 }}>
                        {option.cost === 0 ? 'FREE' : `$${option.cost.toFixed(2)}`}
                      </div>
                    </label>
                  ))}
                </div>
              </Card>
            </div>
            
            {/* Right Column - Order Summary */}
            <div style={{ position: 'sticky', top: 100 }}>
              <Card>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                  Order Summary
                </h2>
                
                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    fontSize: 14
                  }}>
                    <span style={{ color: '#6B7280' }}>
                      Medications ({selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''})
                    </span>
                    <span style={{ fontWeight: 600 }}>
                      ${itemSubtotal.toFixed(2)}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    fontSize: 14
                  }}>
                    <span style={{ color: '#6B7280' }}>
                      Pharmacy Services Fee
                    </span>
                    <span style={{ fontWeight: 600 }}>
                      ${pharmacyFeeTotal.toFixed(2)}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                    fontSize: 14
                  }}>
                    <span style={{ color: '#6B7280' }}>Shipping</span>
                    <span style={{ fontWeight: 600 }}>
                      {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                    </span>
                  </div>
                  
                  <div style={{
                    height: 1,
                    background: '#E5E7EB',
                    margin: '16px 0'
                  }} />
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 4
                  }}>
                    <div>
                      <span style={{ fontSize: 20, fontWeight: 700 }}>Subtotal</span>
                    </div>
                    <span style={{ fontSize: 20, fontWeight: 700, color: brand.c.accent }}>
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 12 }}>
                    *Includes Pharmacy Services Fee
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 20,
                    fontWeight: 700
                  }}>
                    <span>Total</span>
                    <span style={{ color: brand.c.accent }}>
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
                
                <Button
                  icon="check"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={selectedIds.length === 0}
                  style={{ marginBottom: 12 }}
                >
                  {submitting ? 'Placing Order...' : 'Place Order'}
                </Button>
                
                <div style={{
                  fontSize: 12,
                  color: '#6B7280',
                  textAlign: 'center',
                  lineHeight: 1.5
                }}>
                  By placing this order, you agree to our terms of service and privacy policy
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
